import { DistributorRole, type UserRole } from "@models/UserRole";

import type { TScope } from "./scopes";
import { buildRolePermissions, scopeBuilder as s } from "./typeHelpers";

const internalPlanningTool = [
  s.image(),
  s.item_program(),
  s.item(),
  s.note("index"),
  s.planning_tool(),
  s.program(),
];

/** Shared PO actions for purchaser roles (excludes receive, send_to_flow, add_broker_freight). */
const purchaserPurchaseOrderArgs = [
  "index",
  "update",
  "create",
  "delete",
  "submit",
  "cancel",
  "complete",
  "accept",
  "decline",
  "notify",
  "update_delivery_date",
  "convert_to_fulfillment_purchase_orders",
  "remove_variant",
  "update_variant",
  "update_beacon_asset_name",
] as const;

const purchaserPurchaseOrder = s.purchase_order(...purchaserPurchaseOrderArgs);

const selectPurchaserPurchaseOrder = s.purchase_order(
  ...purchaserPurchaseOrderArgs,
  "send_to_flow"
);

const superPurchaseOrder = s.purchase_order(
  ...purchaserPurchaseOrderArgs,
  "send_to_flow",
  "receive"
);

const roleScopes: Record<UserRole, Set<TScope>> = {
  super: buildRolePermissions(
    s.profile(),
    s.admin(),
    s.anaplan_troubleshooting(),
    s.planning_tool(),
    s.order_calendar_month(),
    s.order_window_review(),
    s.address(),
    s.address_change_request(),
    s.address_favorite_set(),
    s.distributor_addresses(),
    s.bid(),
    s.brand(),
    s.budget(),
    s.business_unit(),
    s.change(),
    s.cloudinary_file(),
    s.compliance_override(),
    s.compliance_rules(),
    s.compliance_states(),
    s.coupon_type(),
    s.distributor(),
    s.historical_item(),
    s.historical_order_item(),
    s.image(),
    s.imperial(),
    s.item(),
    s.item_type(),
    s.item_program(),
    s.item_program_variable_pricing(),
    s.note(),
    s.order(),
    s.order_set(),
    s.program(),
    superPurchaseOrder,
    s.purchase_order_variant(),
    s.request_for_price(),
    s.request_for_quote(),
    s.sales_group(),
    s.territory(),
    s.triggered_rule(),
    s.user(),
    s.variable_pricing(),
    s.quote("index"),
    s.supplier_report(),
    s.shipping_parameter(),
    s.shipping_parameter_variant(),
    s.state(),
    s.standard_spec(),
    s.supplier(),
    s.variant_allocation(),
    s.variant_option(),
    s.sku_migration()
  ),
  // Explicit list: central shares many purchaser reads + compliance powers, but
  // BE excludes central from most PO/RFP/OCM/IPV writes and from @super-only creates.
  central: buildRolePermissions(
    s.profile(),
    s.anaplan_troubleshooting(),
    s.planning_tool(),
    s.order_calendar_month("index"),
    // Central can view Order Window Review but cannot set/update decisions (DEV-1359 matrix)
    s.order_window_review("index", "items", "orders", "comments"),
    s.address(),
    s.address_change_request(),
    s.address_favorite_set(),
    s.distributor_addresses(),
    s.bid(),
    s.brand("index"),
    s.budget(),
    s.business_unit(),
    s.change(),
    s.cloudinary_file(),
    s.compliance_override(),
    s.compliance_rules(
      "index",
      "create",
      "update",
      "approve",
      "deny",
      "resend"
    ),
    s.compliance_states(),
    s.coupon_type(),
    s.distributor(),
    s.historical_item(),
    s.historical_order_item(),
    s.image(),
    s.imperial(),
    s.item(),
    s.item_type("index"),
    s.item_program(),
    s.item_program_variable_pricing("index"),
    s.note(),
    s.order(),
    s.order_set(),
    s.program(),
    s.request_for_quote(),
    s.territory("index"),
    s.triggered_rule(),
    s.user("index"),
    s.variable_pricing("index"),
    s.quote("index"),
    s.supplier_report(),
    s.shipping_parameter(),
    s.shipping_parameter_variant(),
    s.state(),
    s.standard_spec(),
    s.supplier("index"),
    s.variant_allocation(),
    s.variant_option("index")
  ),
  creative: buildRolePermissions(
    s.profile("view"),
    ...internalPlanningTool,
    s.address("index"),
    s.address_favorite_set("index"),
    s.brand("index"),
    s.budget("index"),
    s.change("index"),
    s.cloudinary_file(),
    s.distributor("orders"),
    s.item_type("index"),
    s.order("index"),
    s.standard_spec("index"),
    s.supplier("index"),
    s.supplier_report()
  ),
  purchaser: buildRolePermissions(
    s.profile(),
    s.admin(),
    s.planning_tool(),
    s.order_calendar_month(),
    // Purchaser can view Order Window Review but cannot set/update decisions (DEV-1359 matrix)
    s.order_window_review("index", "items", "orders", "comments"),
    s.address(),
    s.address_change_request(),
    s.address_favorite_set(),
    s.distributor_addresses(),
    s.bid(),
    s.budget(),
    s.business_unit(),
    s.change(),
    s.cloudinary_file(),
    s.compliance_rules("index"),
    s.coupon_type(),
    s.distributor(),
    s.historical_item(),
    s.historical_order_item(),
    s.image(),
    s.imperial(),
    s.item(),
    s.item_program(),
    s.item_program_variable_pricing(),
    s.note(),
    s.order("index", "create", "update", "delete", "update_address", "cancel"),
    s.order_set(),
    s.program(),
    purchaserPurchaseOrder,
    s.purchase_order_variant(),
    s.request_for_price(),
    s.request_for_quote(),
    s.triggered_rule("index"),
    s.quote("index"),
    s.standard_spec(),
    s.supplier_report(),
    s.shipping_parameter(),
    s.shipping_parameter_variant(),
    s.state(),
    s.supplier("index"),
    s.variant_allocation()
  ),
  field1: buildRolePermissions(
    s.profile("view"),
    s.address("index", "create", "update", "upsert_user_attn"),
    s.address_change_request("index"),
    s.address_favorite_set(),
    s.bid("create", "delete"),
    s.brand("index"),
    s.budget(),
    s.business_unit("index"),
    s.coupon_type("index"),
    s.distributor_addresses("email:manage"),
    s.historical_item("index"),
    s.historical_order_item("index"),
    s.item("index", "full_item_specs"),
    s.item_program("index"),
    s.item_program_variable_pricing("index"),
    s.item_type("index"),
    s.order("index", "create", "update", "delete"),
    s.order_calendar_month("index"),
    s.order_set("index", "create", "update", "delete", "submit"),
    s.program("index"),
    s.compliance_rules("index"),
    s.state("index"),
    s.supplier("index"),
    s.territory("index"),
    s.triggered_rule("index"),
    s.user("index"),
    s.variable_pricing("index"),
    s.variant_allocation("index"),
    s.variant_option("index")
  ),
  field2: buildRolePermissions(
    s.profile("view"),
    s.planning_tool(),
    s.address("index", "create", "update", "upsert_user_attn"),
    s.address_change_request("index"),
    s.address_favorite_set(),
    s.bid("create", "delete"),
    s.brand("index"),
    s.budget(),
    s.business_unit("index"),
    s.change("index"),
    s.coupon_type("index"),
    s.distributor_addresses("email:manage"),
    s.historical_item("index"),
    s.historical_order_item("index"),
    s.image(),
    s.imperial("initiate_punchout"),
    s.item("index", "create", "update", "full_item_specs"),
    s.item_program(),
    s.item_program_variable_pricing(),
    s.item_type("index"),
    s.note(),
    s.order("index", "create", "update", "delete"),
    s.order_calendar_month("index"),
    // Field 2 can view Order Window Review but cannot set/update decisions (DEV-1359 matrix)
    s.order_window_review("index", "items", "orders"),
    s.order_set(
      "index",
      "create",
      "update",
      "delete",
      "approve_deny",
      "submit"
    ),
    s.program("index", "create", "update", "clone", "submit", "cancel"),
    s.purchase_order("update_variant"),
    s.purchase_order_variant("update"),
    s.request_for_quote("index"),
    s.quote("index"),
    s.compliance_rules("index"),
    s.shipping_parameter("index", "create", "update"),
    s.state("index"),
    s.standard_spec("index"),
    s.supplier("index"),
    s.territory("index"),
    s.triggered_rule("index"),
    s.user("index"),
    s.variable_pricing("index"),
    s.variant_allocation("index"),
    s.variant_option("index")
  ),
  "select-purchaser": buildRolePermissions(
    s.profile(),
    s.admin(),
    s.planning_tool(),
    s.order_calendar_month(),
    // select-purchaser has no access to Order Window Review (DEV-1359 matrix)
    s.address(),
    s.address_change_request(),
    s.address_favorite_set(),
    s.distributor_addresses(),
    s.bid(),
    s.budget(),
    s.business_unit(),
    s.change(),
    s.cloudinary_file(),
    s.compliance_rules("index"),
    s.coupon_type(),
    s.distributor(),
    s.historical_item(),
    s.historical_order_item(),
    s.image(),
    s.imperial(),
    s.item(),
    s.item_program(),
    s.item_program_variable_pricing(),
    s.note(),
    s.order("index", "create", "update", "delete", "update_address", "cancel"),
    s.order_set(),
    s.program(),
    selectPurchaserPurchaseOrder,
    s.purchase_order_variant(),
    s.request_for_price(),
    s.request_for_quote(),
    s.triggered_rule("index"),
    s.quote("index"),
    s.standard_spec(),
    s.supplier_report(),
    s.shipping_parameter(),
    s.shipping_parameter_variant(),
    s.state(),
    s.supplier("index"),
    s.variant_allocation()
  ),
  compliance: buildRolePermissions(
    s.profile("view"),
    s.address("index", "create", "update", "upsert_user_attn"),
    s.address_change_request("index"),
    s.address_favorite_set(),
    s.brand("index"),
    s.budget(),
    s.business_unit("index"),
    s.compliance_override(),
    s.compliance_rules(
      "index",
      "create",
      "update",
      "approve",
      "deny",
      "deactivate"
    ),
    s.compliance_states("update"),
    s.coupon_type("index"),
    s.historical_item("index"),
    s.historical_order_item("index"),
    s.item("index", "full_item_specs"),
    s.item_program_variable_pricing("index"),
    s.item_type("index"),
    s.order("index", "create", "update", "delete", "update_address", "cancel"),
    s.order_calendar_month("index"),
    s.order_set("index", "create", "update", "approve_deny", "submit"),
    s.program("index"),
    s.shipping_parameter("index", "create", "update"),
    s.state("index"),
    s.supplier("index"),
    s.territory("index"),
    s.triggered_rule(),
    s.user("index"),
    s.variable_pricing("index"),
    s.variant_allocation("index"),
    s.variant_option("index")
  ),
  supplier: buildRolePermissions(
    s.profile("view"),
    s.bid("index", "update", "accept", "decline"),
    s.brand("index"),
    s.cloudinary_file(),
    s.cost(),
    s.coupon_type("index"),
    s.item_program_variable_pricing("index"),
    s.item_type("index"),
    s.note(),
    s.program("index"),
    s.purchase_order(
      "index",
      "update",
      "complete",
      "accept",
      "decline",
      "notify"
    ),
    s.purchase_order_variant("index", "update"),
    s.request_for_price("index", "update", "complete"),
    s.request_for_quote("index"),
    s.quote(),
    s.shipping_parameter("index", "update"),
    s.shipping_parameter_variant("index", "add_shipping_extras"),
    s.supplier_report("index")
  ),
  "planning-tool-supplier": buildRolePermissions(
    s.profile("view"),
    s.planning_tool(),
    s.address("index"),
    s.address_favorite_set("index"),
    s.bid("index", "update", "accept", "decline"),
    s.brand("index"),
    s.cloudinary_file(),
    s.cost(),
    s.coupon_type("index"),
    s.image(),
    s.item("index", "create", "update"),
    s.item_program(),
    s.item_program_variable_pricing(),
    s.item_type("index"),
    s.note(),
    s.order("index", "create", "update", "delete"),
    s.order_calendar_month("index"),
    s.order_set("index", "create", "update", "delete", "submit"),
    s.program("index"),
    s.purchase_order(
      "index",
      "create",
      "update",
      "delete",
      "submit",
      "cancel",
      "complete",
      "accept",
      "decline",
      "notify",
      "update_delivery_date"
    ),
    s.purchase_order_variant("index", "update"),
    s.request_for_price("index", "update", "complete"),
    s.request_for_quote("index"),
    s.quote(),
    s.shipping_parameter("index", "update"),
    s.shipping_parameter_variant("index", "add_shipping_extras"),
    s.standard_spec("index"),
    s.supplier("index"),
    s.supplier_report("index"),
    s.user("index"),
    s.variable_pricing("index"),
    s.variant_option("index")
  ),
  "freight-forwarder": buildRolePermissions(
    s.profile("view"),
    s.purchase_order("index", "add_broker_freight")
  ),
  "read-only": buildRolePermissions(
    s.address("index"),
    s.address_change_request("index"),
    s.address_favorite_set(),
    s.bid("index", "create", "delete"),
    s.brand("index"),
    s.budget(),
    s.business_unit("index"),
    s.coupon_type("index"),
    s.historical_item("index"),
    s.historical_order_item("index"),
    s.item("index"),
    s.item_program_variable_pricing("index"),
    s.item_type("index"),
    s.order("index"),
    s.order_calendar_month("index"),
    s.order_set("index"),
    s.program("index"),
    s.request_for_price("index"),
    s.compliance_rules("index"),
    s.shipping_parameter("index", "create", "update"),
    s.shipping_parameter_variant("index"),
    s.state("index"),
    s.supplier("index"),
    s.territory("index"),
    s.triggered_rule("index"),
    s.user("index"),
    s.variable_pricing("index"),
    s.variant_allocation("index"),
    s.variant_option("index")
  ),
  [DistributorRole.Gallo]: buildRolePermissions(
    s.address("index", "create", "update"),
    s.distributor(
      "admin",
      "admin:user:create",
      "admin:user:update",
      "admin:user:delete",
      "admin:organization_user:create",
      "admin:organization_user:update",
      "admin:organization_user:delete",
      "orders:inventory",
      "orders:on_demand"
    ),
    s.item("index", "full_item_specs"),
    s.profile("view"),
    s.order("index", "create"),
    s.order_set("distributor_review")
  ),
  [DistributorRole.DistributorAdmin]: buildRolePermissions(
    s.address("index", "create", "update"),
    s.distributor(
      "admin",
      "admin:user:create",
      "admin:user:update",
      "admin:user:delete",
      "admin:organization_user:branches:update",
      "orders:inventory",
      "orders:on_demand"
    ),
    s.item("index", "full_item_specs"),
    s.profile("view"),
    s.order("index", "create")
  ),
  [DistributorRole.DistributorOrderer]: buildRolePermissions(
    s.address("index", "create", "update"),
    s.distributor(
      "admin",
      "admin:organization_user:branches:update",
      "orders:inventory",
      "orders:on_demand"
    ),
    s.item("index", "full_item_specs"),
    s.order("index", "create"),
    s.profile("view")
  ),
};

export default roleScopes;
